import React from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from '@nextui-org/react';
import { ChangeSummary } from '../../services/sessionStorageService';

interface SummaryDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => Promise<void>;
    onDiscard: () => void;
    changes: ChangeSummary;
    isSaving: boolean;
}

const SummaryDialog: React.FC<SummaryDialogProps> = ({
    isOpen,
    onClose,
    onSave,
    onDiscard,
    changes,
    isSaving
}) => {
    const getFormattedChanges = (): string[] => {
        const summaries: string[] = [];

        if (changes.completionStatus) {
            const { from, to } = changes.completionStatus;
            summaries.push(`Completion status: ${from ? 'Complete' : 'Incomplete'} → ${to ? 'Complete' : 'Incomplete'}`);
        }

        if (changes.isFavorited) {
            const { from, to } = changes.isFavorited;
            summaries.push(`Favorite status: ${from ? 'Favorited' : 'Not favorited'} → ${to ? 'Favorited' : 'Not favorited'}`);
        }

        if (changes.notes) {
            const { from, to } = changes.notes;
            if (!from && to) {
                summaries.push('Notes: Added new content');
            } else if (from && !to) {
                summaries.push('Notes: Removed content');
            } else {
                summaries.push('Notes: Updated content');
            }
        }

        return summaries;
    };

    const formattedChanges = getFormattedChanges();

    return (
        <Modal
            isOpen={isOpen}
            onOpenChange={onClose}
            size="md"
            placement="center"
            classNames={{
                backdrop: "bg-black/50",
                base: "border border-default-200",
                header: "border-b border-default-200",
                footer: "border-t border-default-200"
            }}
        >
            <ModalContent>
                {(onClose) => (
                    <>
                        <ModalHeader className="flex flex-col gap-1">
                            <h3 className="text-lg font-semibold text-darkBlue">
                                Save Changes?
                            </h3>
                        </ModalHeader>
                        <ModalBody>
                            <div className="space-y-4">
                                <p className="text-default-600">
                                    You have made the following changes during this session:
                                </p>

                                {formattedChanges.length > 0 ? (
                                    <div className="bg-default-50 rounded-lg p-4 space-y-2">
                                        {formattedChanges.map((change, index) => (
                                            <div key={index} className="flex items-start gap-2">
                                                <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                                                <span className="text-sm text-default-700">{change}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-warning-50 rounded-lg p-4">
                                        <p className="text-warning-700 text-sm">No changes detected</p>
                                    </div>
                                )}

                                <p className="text-default-500 text-sm">
                                    Would you like to save these changes to your account?
                                </p>
                            </div>
                        </ModalBody>
                        <ModalFooter className="flex gap-2">
                            <Button
                                color="danger"
                                variant="light"
                                onPress={onDiscard}
                                isDisabled={isSaving}
                            >
                                Discard Changes
                            </Button>
                            <Button
                                color="primary"
                                onPress={onSave}
                                isDisabled={isSaving}
                                startContent={isSaving ? <Spinner size="sm" /> : undefined}
                            >
                                {isSaving ? 'Saving...' : 'Save All Changes'}
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
};

export default SummaryDialog; 